/**
 * Aprobar o rechazar varias solicitudes pendientes a la vez: las seleccionadas
 * o, si no hay selección, todas las visibles (respeta el filtro Mi grupo /
 * Todas). Un solo modal con tres pasos: confirmar → avance → resumen.
 * La ejecución es en serie (ver lib/bulk_resolve.js).
 */

import React, { useState } from 'react';
import { Button, Modal, Input, Progress, Space, Typography, Alert, List } from 'antd';

import { runInSeries, previewNames, summarize } from '../../lib/bulk_resolve';

const COPY = {
  approve: {
    title: (n) => `Aprobar ${n === 1 ? '1 solicitud' : `${n} solicitudes`}`,
    body: (n) => `Se ${n === 1 ? 'eliminará 1 elemento' : `eliminarán ${n} elementos`} y quedarán en la papelera.`,
    ok: (n) => `Aprobar y eliminar ${n}`,
    running: 'Aprobando',
  },
  reject: {
    title: (n) => `Rechazar ${n === 1 ? '1 solicitud' : `${n} solicitudes`}`,
    body: () => 'No se eliminará nada. Quien lo pidió verá la solicitud como rechazada.',
    ok: (n) => `Rechazar ${n}`,
    running: 'Rechazando',
  },
};

export const BulkActions = ({ rows, selectedRows, onClearSelection, onKeepSelected, approveOne, rejectOne, onRunningChange }) => {
  // dialog: null | { mode, items, phase: 'confirm' | 'running' | 'done', done, result }
  const [dialog, setDialog] = useState(null);
  const [reason, setReason] = useState('');

  const hasSelection = selectedRows.length > 0;
  const targets = hasSelection ? selectedRows : rows;
  const n = targets.length;

  const open = (mode) => { setReason(''); setDialog({ mode, items: targets, phase: 'confirm', done: 0 }); };
  const close = () => { if (dialog?.phase !== 'running') setDialog(null); };

  const run = async () => {
    const { mode, items } = dialog;
    setDialog((d) => ({ ...d, phase: 'running', done: 0 }));
    onRunningChange(true);
    const action = mode === 'approve' ? approveOne : (r) => rejectOne(r, reason);
    const result = await runInSeries(items, action, {
      onProgress: (done) => setDialog((d) => ({ ...d, done })),
    });
    onRunningChange(false);
    // Las que fallaron quedan seleccionadas para reintentar; las demás salen solas.
    onKeepSelected(result.failed.map((f) => f.item.id));
    setDialog((d) => ({ ...d, phase: 'done', result }));
  };

  const scopeLabel = hasSelection ? 'seleccionadas' : 'todas';
  const copy = dialog ? COPY[dialog.mode] : null;
  const total = dialog?.items.length || 0;
  const { names, rest } = dialog ? previewNames(dialog.items) : { names: [], rest: 0 };

  const footer = () => {
    if (!dialog) return null;
    if (dialog.phase === 'confirm') {
      return [
        <Button key="cancel" onClick={close}>Cancelar</Button>,
        <Button key="ok" type="primary" danger={dialog.mode === 'approve'} onClick={run}>{copy.ok(total)}</Button>,
      ];
    }
    if (dialog.phase === 'running') return [<Button key="wait" disabled>Procesando…</Button>];
    return [<Button key="close" type="primary" onClick={close}>Cerrar</Button>];
  };

  return (
    <>
      <Space wrap style={{ marginBottom: 12 }}>
        {hasSelection ? <Typography.Text strong>{n} {n === 1 ? 'seleccionada' : 'seleccionadas'}</Typography.Text> : null}
        <Button type="primary" danger disabled={!n} onClick={() => open('approve')}>
          {`Aprobar ${scopeLabel} (${n})`}
        </Button>
        <Button disabled={!n} onClick={() => open('reject')}>
          {`Rechazar ${scopeLabel} (${n})`}
        </Button>
        {hasSelection ? <Button type="link" onClick={onClearSelection}>Quitar selección</Button> : null}
      </Space>

      <Modal
        open={!!dialog}
        title={copy ? copy.title(total) : null}
        onCancel={close}
        closable={dialog?.phase !== 'running'}
        maskClosable={false}
        keyboard={dialog?.phase !== 'running'}
        footer={footer()}
        destroyOnClose
      >
        {dialog?.phase === 'confirm' ? (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Typography.Text>{copy.body(total)}</Typography.Text>
            <ul style={{ margin: 0, paddingLeft: 20, maxHeight: 220, overflowY: 'auto' }}>
              {names.map((name, i) => <li key={i}>{name}</li>)}
              {rest ? <li><Typography.Text type="secondary">y {rest} más</Typography.Text></li> : null}
            </ul>
            {dialog.mode === 'reject' ? (
              <Input.TextArea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="Motivo del rechazo (opcional, aplica a todas)" />
            ) : null}
          </Space>
        ) : null}

        {dialog?.phase === 'running' ? (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Typography.Text>{copy.running} {dialog.done} de {total}… No cierres esta ventana.</Typography.Text>
            <Progress percent={Math.round((dialog.done / total) * 100)} status="active" />
          </Space>
        ) : null}

        {dialog?.phase === 'done' ? (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Alert
              type={dialog.result.failed.length ? (dialog.result.ok.length ? 'warning' : 'error') : 'success'}
              showIcon
              message={summarize(dialog.mode, dialog.result)}
              description={dialog.mode === 'approve' && dialog.result.ok.length
                ? 'Lo eliminado quedó en la papelera.' : undefined}
            />
            {dialog.result.failed.length ? (
              <>
                <Typography.Text type="secondary">
                  Estas siguen seleccionadas para que puedas revisarlas o reintentar:
                </Typography.Text>
                <List size="small" bordered style={{ maxHeight: 220, overflowY: 'auto' }}
                  dataSource={dialog.result.failed}
                  renderItem={(f) => (
                    <List.Item>
                      <Space direction="vertical" size={0}>
                        <Typography.Text>{f.item.itemName}</Typography.Text>
                        <Typography.Text type="danger" style={{ fontSize: 12 }}>{f.message}</Typography.Text>
                      </Space>
                    </List.Item>
                  )} />
              </>
            ) : null}
          </Space>
        ) : null}
      </Modal>
    </>
  );
};
